/**
 * Support Settings Component (Admin Only)
 *
 * Admin dashboard for viewing and managing:
 * - Support Requests: User-submitted bugs, features, support requests
 * - System Logs: Client-side errors and warnings
 */

import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  AlertTriangle,
  Bug,
  Lightbulb,
  HelpCircle,
  Clock,
  CheckCircle,
  XCircle,
  Archive,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  AlertCircle,
  Info,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import {
  getSupportRequests,
  getClientLogs,
  updateSupportRequest,
  dismissClientLog,
  dismissClientLogsBatch,
} from '@business-assistant/shared';
import type {
  SupportRequest,
  SupportRequestType,
  SupportPriority,
  SupportStatus,
  ClientLog,
  LogLevel,
  LogSource,
} from '@business-assistant/shared';

// =============================================================================
// Types
// =============================================================================

type SupportTab = 'requests' | 'logs';

// =============================================================================
// Main Component
// =============================================================================

export function SupportSettings() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<SupportTab>('requests');

  return (
    <div className="settings-section">
      {/* Tab Switcher */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '1px solid var(--color-border)',
        paddingBottom: '8px',
      }}>
        <button
          onClick={() => setActiveTab('requests')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            border: 'none',
            background: activeTab === 'requests' ? 'var(--color-primary)' : 'transparent',
            color: activeTab === 'requests' ? 'white' : 'var(--color-text)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          <MessageSquare size={16} />
          {t('support.tabs.requests')}
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            border: 'none',
            background: activeTab === 'logs' ? 'var(--color-primary)' : 'transparent',
            color: activeTab === 'logs' ? 'white' : 'var(--color-text)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          <AlertTriangle size={16} />
          {t('support.tabs.logs')}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'requests' && <SupportRequestsTab />}
      {activeTab === 'logs' && <SystemLogsTab />}
    </div>
  );
}

// =============================================================================
// Support Requests Tab
// =============================================================================

function SupportRequestsTab() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters - only search for client-side (admin filters are in marta-admin)
  const [searchQuery, setSearchQuery] = useState('');

  // Admin note state
  const [noteText, setNoteText] = useState<Record<string, string>>({});
  const [isSavingNote, setIsSavingNote] = useState<string | null>(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const result = await getSupportRequests(undefined, 100, 0);
      setRequests(result.items);
      setTotalCount(result.total);
    } catch (error) {
      console.error('Failed to load support requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id: string, newStatus: SupportStatus) => {
    try {
      await updateSupportRequest(id, { status: newStatus });
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: newStatus, updatedAt: new Date().toISOString() } : r))
      );
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleSaveNote = async (id: string) => {
    const note = noteText[id];
    if (!note?.trim()) return;

    setIsSavingNote(id);
    try {
      await updateSupportRequest(id, { adminNotes: note.trim() });
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, adminNotes: note.trim(), updatedAt: new Date().toISOString() } : r))
      );
      setNoteText((prev) => ({ ...prev, [id]: '' }));
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSavingNote(null);
    }
  };

  const filteredRequests = requests.filter((r) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      r.subject.toLowerCase().includes(query) ||
      r.description.toLowerCase().includes(query) ||
      r.userName.toLowerCase().includes(query) ||
      r.userEmail.toLowerCase().includes(query)
    );
  });

  const getTypeIcon = (type: SupportRequestType) => {
    switch (type) {
      case 'bug': return <Bug size={16} />;
      case 'feature': return <Lightbulb size={16} />;
      case 'support': return <HelpCircle size={16} />;
      case 'question': return <MessageSquare size={16} />;
    }
  };

  const getStatusColor = (status: SupportStatus) => {
    switch (status) {
      case 'open': return 'var(--color-primary)';
      case 'in_progress': return 'var(--color-warning, #f59e0b)';
      case 'resolved': return 'var(--color-success, #10b981)';
      case 'closed': return 'var(--color-text-secondary)';
    }
  };

  const getPriorityColor = (priority: SupportPriority) => {
    switch (priority) {
      case 'low': return 'var(--color-text-secondary)';
      case 'medium': return 'var(--color-primary)';
      case 'high': return 'var(--color-warning, #f59e0b)';
      case 'urgent': return 'var(--color-error, #ef4444)';
    }
  };

  return (
    <div className="settings-panel">
      <div className="settings-panel-header">
        <MessageSquare size={18} />
        <span>{t('support.requests.title')}</span>
        <span style={{ marginLeft: 'auto', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          {totalCount} total
        </span>
      </div>

      <div className="settings-panel-content">
        {/* Search */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
              <input
                type="text"
                className="settings-input"
                placeholder={t('support.requests.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: '36px' }}
              />
            </div>
          </div>

          <button
            onClick={loadRequests}
            className="btn-secondary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Loader2 size={32} className="spinner" style={{ color: 'var(--color-primary)' }} />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredRequests.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
            <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>{t('support.requests.noRequests')}</p>
            <p style={{ margin: '8px 0 0', fontSize: '14px' }}>{t('support.requests.noRequestsHint')}</p>
          </div>
        )}

        {/* Request List */}
        {!isLoading && filteredRequests.map((request) => (
          <div
            key={request.id}
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              marginBottom: '12px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'var(--color-bg-secondary)',
                cursor: 'pointer',
              }}
            >
              {expandedId === request.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}

              <span style={{ color: getPriorityColor(request.priority) }}>
                {getTypeIcon(request.type)}
              </span>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {request.subject}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                  {request.userName} &bull; {new Date(request.createdAt).toLocaleDateString()}
                </div>
              </div>

              <span
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 500,
                  background: `${getStatusColor(request.status)}20`,
                  color: getStatusColor(request.status),
                }}
              >
                {t(`support.statuses.${request.status}`)}
              </span>

              <span
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 500,
                  background: `${getPriorityColor(request.priority)}20`,
                  color: getPriorityColor(request.priority),
                }}
              >
                {t(`support.priorities.${request.priority}`)}
              </span>
            </div>

            {/* Expanded Content */}
            {expandedId === request.id && (
              <div style={{ padding: '16px', borderTop: '1px solid var(--color-border)' }}>
                {/* Description */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                    {t('common.description')}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{request.description}</div>
                </div>

                {/* Metadata */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{t('support.detail.submittedBy')}</div>
                    <div>{request.userName} ({request.userEmail})</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{t('support.detail.browserInfo')}</div>
                    <div>{request.browserInfo}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{t('support.detail.submittedAt')}</div>
                    <div>{new Date(request.createdAt).toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{t('support.detail.pageUrl')}</div>
                    <a href={request.currentUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      View page <ExternalLink size={12} />
                    </a>
                  </div>
                </div>

                {/* Attachment */}
                {request.attachmentUrl && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>{t('support.reportIssue.attachment')}</div>
                    <a href={request.attachmentUrl} target="_blank" rel="noopener noreferrer">
                      <img src={request.attachmentUrl} alt="Attachment" style={{ maxWidth: '300px', borderRadius: '8px' }} />
                    </a>
                  </div>
                )}

                {/* Admin Notes */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>{t('support.detail.adminNotes')}</div>
                  {request.adminNotes && (
                    <div style={{ padding: '12px', background: 'var(--color-bg-secondary)', borderRadius: '8px', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
                      {request.adminNotes}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      className="settings-input"
                      placeholder={t('support.requests.notePlaceholder')}
                      value={noteText[request.id] || ''}
                      onChange={(e) => setNoteText((prev) => ({ ...prev, [request.id]: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                    <button
                      onClick={() => handleSaveNote(request.id)}
                      disabled={isSavingNote === request.id || !noteText[request.id]?.trim()}
                      className="btn-secondary btn-sm"
                    >
                      {isSavingNote === request.id ? <Loader2 size={14} className="spinner" /> : t('support.requests.saveNote')}
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {request.status === 'open' && (
                    <button
                      onClick={() => handleStatusChange(request.id, 'in_progress')}
                      className="btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Clock size={14} />
                      Start Progress
                    </button>
                  )}
                  {(request.status === 'open' || request.status === 'in_progress') && (
                    <button
                      onClick={() => handleStatusChange(request.id, 'resolved')}
                      className="btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-success)' }}
                    >
                      <CheckCircle size={14} />
                      {t('support.requests.resolve')}
                    </button>
                  )}
                  {request.status !== 'closed' && (
                    <button
                      onClick={() => handleStatusChange(request.id, 'closed')}
                      className="btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <XCircle size={14} />
                      {t('support.requests.close')}
                    </button>
                  )}
                  {request.status === 'closed' && (
                    <button
                      onClick={() => handleStatusChange(request.id, 'open')}
                      className="btn-secondary btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <RefreshCw size={14} />
                      {t('support.requests.reopen')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// System Logs Tab
// =============================================================================

function SystemLogsTab() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<ClientLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filters
  const [levelFilter, setLevelFilter] = useState<LogLevel | ''>('');
  const [sourceFilter, setSourceFilter] = useState<LogSource | ''>('');
  const [showDismissed, setShowDismissed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadLogs();
  }, [levelFilter, sourceFilter, showDismissed]);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const filters: Record<string, unknown> = {};
      if (levelFilter) filters.level = levelFilter;
      if (sourceFilter) filters.source = sourceFilter;
      if (!showDismissed) filters.dismissed = false;

      const result = await getClientLogs(filters, 100, 0);
      setLogs(result.items);
      setTotalCount(result.total);
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await dismissClientLog(id, true);
      setLogs((prev) => prev.map((l) => (l.id === id ? { ...l, dismissed: true } : l)));
    } catch (error) {
      console.error('Failed to dismiss log:', error);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await dismissClientLog(id, false);
      setLogs((prev) => prev.map((l) => (l.id === id ? { ...l, dismissed: false } : l)));
    } catch (error) {
      console.error('Failed to restore log:', error);
    }
  };

  const handleDismissSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await dismissClientLogsBatch(Array.from(selectedIds), true);
      setLogs((prev) => prev.map((l) => (selectedIds.has(l.id) ? { ...l, dismissed: true } : l)));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to dismiss logs:', error);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const filteredLogs = logs.filter((l) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return l.message.toLowerCase().includes(query) || l.url.toLowerCase().includes(query);
  });

  const getLevelIcon = (level: LogLevel) => {
    switch (level) {
      case 'error': return <AlertCircle size={16} style={{ color: 'var(--color-error, #ef4444)' }} />;
      case 'warn': return <AlertTriangle size={16} style={{ color: 'var(--color-warning, #f59e0b)' }} />;
      case 'info': return <Info size={16} style={{ color: 'var(--color-primary)' }} />;
    }
  };

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case 'error': return 'var(--color-error, #ef4444)';
      case 'warn': return 'var(--color-warning, #f59e0b)';
      case 'info': return 'var(--color-primary)';
    }
  };

  return (
    <div className="settings-panel">
      <div className="settings-panel-header">
        <AlertTriangle size={18} />
        <span>{t('support.logs.title')}</span>
        <span style={{ marginLeft: 'auto', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          {totalCount} total
        </span>
      </div>

      <div className="settings-panel-content">
        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
              <input
                type="text"
                className="settings-input"
                placeholder={t('support.logs.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: '36px' }}
              />
            </div>
          </div>

          <select
            className="settings-select"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LogLevel | '')}
            style={{ minWidth: '150px' }}
          >
            <option value="">{t('support.logs.filterByLevel')}</option>
            <option value="error">{t('support.logs.levels.error')}</option>
            <option value="warn">{t('support.logs.levels.warn')}</option>
            <option value="info">{t('support.logs.levels.info')}</option>
          </select>

          <select
            className="settings-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as LogSource | '')}
            style={{ minWidth: '150px' }}
          >
            <option value="">{t('support.logs.filterBySource')}</option>
            <option value="console">{t('support.logs.sources.console')}</option>
            <option value="unhandled">{t('support.logs.sources.unhandled')}</option>
            <option value="promise_rejection">{t('support.logs.sources.promise_rejection')}</option>
            <option value="boundary">{t('support.logs.sources.boundary')}</option>
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)}
            />
            {showDismissed ? t('support.logs.hideDismissed') : t('support.logs.showDismissed')}
          </label>

          <button
            onClick={loadLogs}
            className="btn-secondary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Bulk Actions */}
        {selectedIds.size > 0 && (
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px',
            background: 'var(--color-bg-secondary)',
            borderRadius: '8px',
          }}>
            <span>{selectedIds.size} selected</span>
            <button
              onClick={handleDismissSelected}
              className="btn-secondary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Archive size={14} />
              {t('support.logs.dismissSelected')}
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Loader2 size={32} className="spinner" style={{ color: 'var(--color-primary)' }} />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredLogs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
            <AlertTriangle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>{t('support.logs.noLogs')}</p>
            <p style={{ margin: '8px 0 0', fontSize: '14px' }}>{t('support.logs.noLogsHint')}</p>
          </div>
        )}

        {/* Logs List */}
        {!isLoading && filteredLogs.map((log) => (
          <div
            key={log.id}
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              marginBottom: '8px',
              overflow: 'hidden',
              opacity: log.dismissed ? 0.6 : 1,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: `${getLevelColor(log.level)}10`,
                cursor: 'pointer',
              }}
              onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(log.id)}
                onChange={() => toggleSelect(log.id)}
                onClick={(e) => e.stopPropagation()}
              />

              {expandedId === log.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}

              {getLevelIcon(log.level)}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {log.message}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                  {t(`support.logs.sources.${log.source}`)} &bull; {new Date(log.timestamp).toLocaleString()}
                </div>
              </div>

              {log.dismissed ? (
                <button
                  onClick={(e) => { e.stopPropagation(); handleRestore(log.id); }}
                  className="btn-secondary btn-sm"
                  title={t('support.logs.restore')}
                >
                  <RefreshCw size={14} />
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDismiss(log.id); }}
                  className="btn-secondary btn-sm"
                  title={t('support.logs.dismiss')}
                >
                  <Archive size={14} />
                </button>
              )}
            </div>

            {/* Expanded Content */}
            {expandedId === log.id && (
              <div style={{ padding: '16px', borderTop: '1px solid var(--color-border)' }}>
                {/* Full Message */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Message</div>
                  <pre style={{
                    background: 'var(--color-bg-secondary)',
                    padding: '12px',
                    borderRadius: '8px',
                    overflow: 'auto',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}>
                    {log.message}
                  </pre>
                </div>

                {/* Stack Trace */}
                {log.stack && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>{t('support.detail.viewStack')}</div>
                    <pre style={{
                      background: 'var(--color-bg-secondary)',
                      padding: '12px',
                      borderRadius: '8px',
                      overflow: 'auto',
                      fontSize: '11px',
                      fontFamily: 'monospace',
                      maxHeight: '200px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}>
                      {log.stack}
                    </pre>
                  </div>
                )}

                {/* Metadata */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>URL</div>
                    <div style={{ fontSize: '13px', wordBreak: 'break-all' }}>{log.url}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>User Agent</div>
                    <div style={{ fontSize: '13px', wordBreak: 'break-all' }}>{log.userAgent}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>User ID</div>
                    <div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{log.userId}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Timestamp</div>
                    <div style={{ fontSize: '13px' }}>{new Date(log.timestamp).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default SupportSettings;
